import type { AuthProvider } from "ra-core";
import { supabaseAuthProvider } from "ra-supabase-core";

import { canAccess } from "../commons/canAccess";
import { getSupabaseClient } from "./supabase";

const getBaseAuthProvider = () =>
  supabaseAuthProvider(getSupabaseClient(), {
    getIdentity: async () => {
      const sale = await getSale();

      if (sale == null) {
        throw new Error();
      }

      return {
        id: sale.id,
        fullName: `${sale.first_name} ${sale.last_name}`,
        avatar: sale.avatar?.src,
      };
    },
  });

// To speed up checks, we cache the initialization state
// and the current sale in the local storage. They are cleared on logout.
const IS_INITIALIZED_CACHE_KEY = "RaStore.auth.is_initialized";
const CURRENT_SALE_CACHE_KEY = "RaStore.auth.current_sale";
const PERMISSIONS_CACHE_KEY = "RaStore.auth.permissions";

function getLocalStorage(): Storage | null {
  if (typeof window !== "undefined" && window.localStorage) {
    return window.localStorage;
  }
  return null;
}

export async function getIsInitialized() {
  const storage = getLocalStorage();
  const cachedValue = storage?.getItem(IS_INITIALIZED_CACHE_KEY);
  if (cachedValue != null) {
    return cachedValue === "true";
  }

  const { data } = await getSupabaseClient()
    .from("init_state")
    .select("is_initialized");
  const isInitialized = data?.at(0)?.is_initialized > 0;

  if (isInitialized) {
    storage?.setItem(IS_INITIALIZED_CACHE_KEY, "true");
  }

  return isInitialized;
}

const getSale = async () => {
  const storage = getLocalStorage();
  const cachedValue = storage?.getItem(CURRENT_SALE_CACHE_KEY);
  if (cachedValue != null) {
    return JSON.parse(cachedValue);
  }

  const { data: dataSession, error: errorSession } =
    await getSupabaseClient().auth.getSession();

  // Shouldn't happen after login but just in case
  if (dataSession?.session?.user == null || errorSession) {
    return undefined;
  }

  const { data: dataSale, error: errorSale } = await getSupabaseClient()
    .from("sales")
    .select("id, first_name, last_name, avatar, administrator")
    .match({ user_id: dataSession?.session?.user.id })
    .single();

  // Shouldn't happen either as all users are sales but just in case
  if (dataSale == null || errorSale) {
    return undefined;
  }

  storage?.setItem(CURRENT_SALE_CACHE_KEY, JSON.stringify(dataSale));
  return dataSale;
};

/**
 * The permission ids the current user holds, read from the `my_permissions`
 * view. That view runs the same has_permission() the RLS policies run, so the
 * UI never has to reimplement the rules or guess at them.
 */
/**
 * How long a cached permission set is trusted.
 *
 * Permissions change when an admin changes a role, which the holder cannot
 * know about. Without an expiry the cache was kept until an explicit logout, so
 * a role granted this morning might not take effect for days. Five minutes is
 * short enough that a change lands within a coffee break and long enough that
 * the view is not queried on every navigation.
 */
const PERMISSIONS_TTL_MS = 5 * 60 * 1000;

const getPermissions = async (): Promise<ReadonlySet<string>> => {
  const storage = getLocalStorage();
  const cached = storage?.getItem(PERMISSIONS_CACHE_KEY);
  if (cached != null) {
    try {
      const parsed = JSON.parse(cached);
      // The old format was a bare array. Treating it as expired rather than
      // trying to read it means an upgrade heals itself on the next load.
      if (
        parsed &&
        typeof parsed === "object" &&
        Array.isArray(parsed.ids) &&
        typeof parsed.at === "number" &&
        Date.now() - parsed.at < PERMISSIONS_TTL_MS
      ) {
        return new Set<string>(parsed.ids);
      }
    } catch {
      // Unreadable cache is no cache. Falling through re-fetches.
    }
  }

  const { data, error } = await getSupabaseClient()
    .from("my_permissions")
    .select("permission_id");

  // No row means no permission. Failing closed is the point: a user with a
  // broken session must not be handed a UI that implies access.
  if (error || data == null) return new Set<string>();

  const ids = data.map((row: { permission_id: string }) => row.permission_id);

  // An empty result is never cached. A user with genuinely no permissions has
  // nothing to speed up, and every other cause -- a role assigned seconds ago,
  // a view that briefly failed -- would otherwise stick until the next logout.
  if (ids.length > 0) {
    storage?.setItem(
      PERMISSIONS_CACHE_KEY,
      JSON.stringify({ ids, at: Date.now() }),
    );
  }
  return new Set<string>(ids);
};

function clearCache() {
  const storage = getLocalStorage();
  storage?.removeItem(IS_INITIALIZED_CACHE_KEY);
  storage?.removeItem(CURRENT_SALE_CACHE_KEY);
  storage?.removeItem(PERMISSIONS_CACHE_KEY);
}

export const getAuthProvider = (): AuthProvider => {
  const baseAuthProvider = getBaseAuthProvider();
  return {
    ...baseAuthProvider,
    login: async (params) => {
      // Clearing on the way in as well as the way out. The cache was cleared
      // only on logout, so a session that ended any other way -- an expired
      // token, a closed tab, a different person on the same browser -- left the
      // previous permission set in place for whoever logged in next. A user
      // could be denied their own dashboard because of a set that was never
      // theirs, and no amount of fixing their roles would change it.
      clearCache();
      if (params.ssoDomain) {
        const { error } = await getSupabaseClient().auth.signInWithSSO({
          domain: params.ssoDomain,
        });
        if (error) {
          throw error;
        }
        return;
      }
      return baseAuthProvider.login(params);
    },
    logout: async (params) => {
      clearCache();
      return baseAuthProvider.logout(params);
    },
    checkAuth: async (params) => {
      // Users are on the set-password page, nothing to do
      if (
        window.location.pathname === "/set-password" ||
        window.location.hash.includes("#/set-password")
      ) {
        return;
      }
      // Users are on the forgot-password page, nothing to do
      if (
        window.location.pathname === "/forgot-password" ||
        window.location.hash.includes("#/forgot-password")
      ) {
        return;
      }
      // Users are on the sign-up page, nothing to do
      if (
        window.location.pathname === "/sign-up" ||
        window.location.hash.includes("#/sign-up")
      ) {
        return;
      }

      const isInitialized = await getIsInitialized();

      if (!isInitialized) {
        await getSupabaseClient().auth.signOut();
        throw {
          redirectTo: "/sign-up",
          message: false,
        };
      }

      return baseAuthProvider.checkAuth(params);
    },
    checkError: async (error) => {
      // ra-supabase-core rejects on 401 *and* 403, and a rejected checkError
      // logs the user out. PostgREST answers 403 when an RLS policy refuses a
      // write, so without this a client whose ticket is refused -- or any user
      // who touches something their role does not cover -- gets signed out
      // instead of an error message.
      //
      // 403 means "not allowed", which is a fact about the request. Only 401
      // and a missing session say anything about the session itself.
      const status = (error as { status?: number })?.status;
      const name = (error as { name?: string })?.name;

      // Resolving is what keeps the user signed in: ra-core logs out on a
      // rejected checkError and does nothing on a resolved one, leaving the
      // failed mutation to report itself.
      if (status === 403) return;

      if (
        status === 401 ||
        (status === 400 && name === "AuthSessionMissingError")
      ) {
        return baseAuthProvider.checkError(error);
      }

      return;
    },
    canAccess: async (params) => {
      const isInitialized = await getIsInitialized();
      if (!isInitialized) return false;

      // Get the current user
      const sale = await getSale();
      if (sale == null) return false;

      return canAccess(await getPermissions(), params);
    },
    getAuthorizationDetails(authorizationId: string) {
      return getSupabaseClient().auth.oauth.getAuthorizationDetails(
        authorizationId,
      );
    },
    approveAuthorization(authorizationId: string) {
      return getSupabaseClient().auth.oauth.approveAuthorization(
        authorizationId,
      );
    },
    denyAuthorization(authorizationId: string) {
      return getSupabaseClient().auth.oauth.denyAuthorization(authorizationId);
    },
  };
};
